'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, Loader2, RotateCcw, Square, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CONFIRMATION_VIDEO_TARGET_SECONDS } from '@/lib/confirmationVideo';
import {
  CONFIRMATION_VIDEO_RECORD_MS,
  blobToConfirmationVideoFile,
  classifyCameraError,
  isCameraRecordingSupported,
  pickRecorderMimeType,
  stopMediaStream,
} from '@/lib/confirmationVideoRecorder';

export type ConfirmationVideoSourceMode = 'upload' | 'record';

type RecorderPhase =
  | 'idle'
  | 'requesting'
  | 'live'
  | 'recording'
  | 'recorded'
  | 'unsupported'
  | 'error';

type ConfirmationVideoRecorderProps = {
  onFileReady: (file: File) => void;
  onCancel?: () => void;
  disabled?: boolean;
  className?: string;
};

export function ConfirmationVideoSourceToggle({
  mode,
  onChange,
  recordingSupported,
  disabled,
}: {
  mode: ConfirmationVideoSourceMode;
  onChange: (mode: ConfirmationVideoSourceMode) => void;
  recordingSupported: boolean;
  disabled?: boolean;
}) {
  return (
    <div
      className="inline-flex rounded-lg border border-gray-200 dark:border-neutral-700 p-0.5 bg-gray-50 dark:bg-neutral-800/60"
      role="tablist"
      aria-label="Origem do vídeo de confirmação"
    >
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'upload'}
        disabled={disabled}
        onClick={() => onChange('upload')}
        className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
          mode === 'upload'
            ? 'bg-white dark:bg-neutral-900 text-teal-700 dark:text-teal-400 shadow-sm'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
        }`}
      >
        Upload
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'record'}
        disabled={disabled || !recordingSupported}
        title={
          recordingSupported
            ? 'Gravar 3 segundos pela webcam'
            : 'Gravação não suportada neste navegador — use o upload'
        }
        onClick={() => onChange('record')}
        className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors inline-flex items-center gap-1 ${
          mode === 'record'
            ? 'bg-white dark:bg-neutral-900 text-teal-700 dark:text-teal-400 shadow-sm'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-50'
        }`}
      >
        <Camera className="w-3.5 h-3.5" />
        Gravar vídeo
      </button>
    </div>
  );
}

export function ConfirmationVideoRecorder({
  onFileReady,
  onCancel,
  disabled = false,
  className = '',
}: ConfirmationVideoRecorderProps) {
  const liveVideoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordedUrlRef = useRef<string | null>(null);

  const [phase, setPhase] = useState<RecorderPhase>(() =>
    isCameraRecordingSupported() ? 'idle' : 'unsupported',
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [recordedFile, setRecordedFile] = useState<File | null>(null);

  const clearTimers = useCallback(() => {
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    if (tickTimerRef.current) {
      clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
  }, []);

  const revokeRecordedUrl = useCallback(() => {
    if (recordedUrlRef.current) {
      URL.revokeObjectURL(recordedUrlRef.current);
      recordedUrlRef.current = null;
    }
    setRecordedUrl(null);
  }, []);

  const releaseCamera = useCallback(() => {
    clearTimers();
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try {
        recorderRef.current.stop();
      } catch {
        // ignore
      }
    }
    recorderRef.current = null;
    stopMediaStream(streamRef.current);
    streamRef.current = null;
    if (liveVideoRef.current) {
      liveVideoRef.current.srcObject = null;
    }
  }, [clearTimers]);

  useEffect(() => {
    return () => {
      releaseCamera();
      revokeRecordedUrl();
    };
  }, [releaseCamera, revokeRecordedUrl]);

  const attachStreamToVideo = useCallback(async (stream: MediaStream) => {
    const video = liveVideoRef.current;
    if (!video) return;
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    try {
      await video.play();
    } catch {
      // autoplay pode falhar; usuário ainda vê o frame ao gravar
    }
  }, []);

  const startCamera = useCallback(async () => {
    if (disabled) return;
    if (!isCameraRecordingSupported()) {
      setPhase('unsupported');
      setErrorMessage(
        'Seu navegador não suporta gravação pela câmera. Use o upload manual (MP4/WEBM de 3s).',
      );
      return;
    }

    setErrorMessage(null);
    setPhase('requesting');
    revokeRecordedUrl();
    setRecordedFile(null);
    releaseCamera();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;
      setPhase('live');
      // Aguarda o <video> montar no próximo paint
      requestAnimationFrame(() => {
        void attachStreamToVideo(stream);
      });
    } catch (err) {
      const classified = classifyCameraError(err);
      setErrorMessage(classified.message);
      setPhase(classified.kind === 'unsupported' ? 'unsupported' : 'error');
      releaseCamera();
    }
  }, [attachStreamToVideo, disabled, releaseCamera, revokeRecordedUrl]);

  useEffect(() => {
    if (phase === 'live' && streamRef.current) {
      void attachStreamToVideo(streamRef.current);
    }
  }, [phase, attachStreamToVideo]);

  const finishRecording = useCallback(() => {
    clearTimers();
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.requestData?.();
      } catch {
        // optional
      }
      recorder.stop();
    }
  }, [clearTimers]);

  const startRecording = useCallback(() => {
    if (disabled || !streamRef.current) return;
    const mimeType = pickRecorderMimeType();
    if (!mimeType) {
      setPhase('unsupported');
      setErrorMessage(
        'Seu navegador não suporta gravação pela câmera. Use o upload manual (MP4/WEBM de 3s).',
      );
      return;
    }

    chunksRef.current = [];
    setElapsedMs(0);
    setErrorMessage(null);

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(streamRef.current, { mimeType });
    } catch {
      try {
        recorder = new MediaRecorder(streamRef.current);
      } catch (err) {
        const classified = classifyCameraError(err);
        setErrorMessage(classified.message);
        setPhase('error');
        return;
      }
    }

    recorderRef.current = recorder;
    const startedAt = Date.now();

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    recorder.onerror = () => {
      clearTimers();
      setErrorMessage('Falha durante a gravação. Tente novamente ou use o upload manual.');
      setPhase('error');
      releaseCamera();
    };

    recorder.onstop = () => {
      clearTimers();
      const blobType = recorder.mimeType || mimeType || 'video/webm';
      const blob = new Blob(chunksRef.current, { type: blobType });
      chunksRef.current = [];
      const recordedDurationSeconds = Math.max(
        0,
        (Date.now() - startedAt) / 1000,
      );

      // Para o stream ao terminar — preview usa object URL do blob
      stopMediaStream(streamRef.current);
      streamRef.current = null;
      if (liveVideoRef.current) {
        liveVideoRef.current.srcObject = null;
      }
      recorderRef.current = null;

      if (!blob.size) {
        setErrorMessage('A gravação ficou vazia. Tente novamente.');
        setPhase('error');
        return;
      }

      const file = blobToConfirmationVideoFile(
        blob,
        Date.now(),
        recordedDurationSeconds,
      );
      const url = URL.createObjectURL(blob);
      recordedUrlRef.current = url;
      setRecordedUrl(url);
      setRecordedFile(file);
      setPhase('recorded');
      setElapsedMs(CONFIRMATION_VIDEO_RECORD_MS);
    };

    recorder.start(250);
    setPhase('recording');

    tickTimerRef.current = setInterval(() => {
      setElapsedMs(Math.min(CONFIRMATION_VIDEO_RECORD_MS, Date.now() - startedAt));
    }, 100);

    stopTimerRef.current = setTimeout(() => {
      finishRecording();
    }, CONFIRMATION_VIDEO_RECORD_MS);
  }, [clearTimers, disabled, finishRecording, releaseCamera]);

  const handleConfirm = () => {
    if (!recordedFile) return;
    onFileReady(recordedFile);
  };

  const handleRerecord = () => {
    revokeRecordedUrl();
    setRecordedFile(null);
    setElapsedMs(0);
    void startCamera();
  };

  const remainingSec = Math.max(
    0,
    (CONFIRMATION_VIDEO_RECORD_MS - elapsedMs) / 1000,
  ).toFixed(1);
  const progressPct = Math.min(100, (elapsedMs / CONFIRMATION_VIDEO_RECORD_MS) * 100);

  if (phase === 'unsupported') {
    return (
      <div
        className={`rounded-xl border border-amber-200 dark:border-amber-900/40 bg-amber-50/80 dark:bg-amber-950/20 p-4 space-y-2 ${className}`}
        role="status"
      >
        <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
          Gravação pela câmera indisponível
        </p>
        <p className="text-xs text-amber-800 dark:text-amber-300">
          {errorMessage ||
            'Este navegador não oferece suporte a MediaRecorder/getUserMedia. Use a opção Upload com um vídeo MP4/WEBM de 3 segundos.'}
        </p>
        {onCancel && (
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Voltar ao upload
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 overflow-hidden ${className}`}
      aria-live="polite"
    >
      <div className="px-4 py-3 border-b border-gray-100 dark:border-neutral-800 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
          <Video className="w-4 h-4 text-teal-600" />
          Gravar vídeo de {CONFIRMATION_VIDEO_TARGET_SECONDS}s
        </div>
        {phase === 'recording' && (
          <span className="text-xs font-bold text-red-600 tabular-nums">
            Gravando… {remainingSec}s
          </span>
        )}
      </div>

      <div className="p-4 space-y-3">
        {(phase === 'live' || phase === 'recording' || phase === 'requesting') && (
          <div className="relative aspect-video rounded-lg overflow-hidden bg-black">
            <video
              ref={liveVideoRef}
              className="h-full w-full object-cover mirror-x"
              style={{ transform: 'scaleX(-1)' }}
              playsInline
              muted
              autoPlay
            />
            {phase === 'requesting' && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              </div>
            )}
            {phase === 'recording' && (
              <div className="absolute top-3 left-3 flex items-center gap-2 rounded-full bg-red-600/90 text-white text-[10px] font-bold px-2.5 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                REC
              </div>
            )}
          </div>
        )}

        {phase === 'recorded' && recordedUrl && (
          <div className="relative aspect-video rounded-lg overflow-hidden bg-black">
            <video
              src={recordedUrl}
              className="h-full w-full object-contain"
              controls
              playsInline
            />
          </div>
        )}

        {phase === 'idle' && (
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Posicione o rosto na câmera e grave exatamente {CONFIRMATION_VIDEO_TARGET_SECONDS}{' '}
            segundos. O áudio não é capturado.
          </p>
        )}

        {phase === 'recording' && (
          <div className="h-1.5 w-full rounded-full bg-gray-200 dark:bg-neutral-700 overflow-hidden">
            <div
              className="h-full bg-red-500 transition-[width] duration-100"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        )}

        {errorMessage && (
          <p className="text-xs text-red-600 dark:text-red-400" role="alert">
            {errorMessage}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          {phase === 'idle' || phase === 'error' ? (
            <>
              <Button
                type="button"
                onClick={() => void startCamera()}
                disabled={disabled}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                <Camera className="w-4 h-4 mr-1.5" />
                Abrir câmera
              </Button>
              {onCancel && (
                <Button type="button" variant="outline" onClick={onCancel} disabled={disabled}>
                  Usar upload
                </Button>
              )}
            </>
          ) : null}

          {phase === 'live' ? (
            <>
              <Button
                type="button"
                onClick={startRecording}
                disabled={disabled}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Square className="w-3.5 h-3.5 mr-1.5 fill-current" />
                Gravar {CONFIRMATION_VIDEO_TARGET_SECONDS}s
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  releaseCamera();
                  setPhase('idle');
                }}
                disabled={disabled}
              >
                Fechar câmera
              </Button>
            </>
          ) : null}

          {phase === 'recording' ? (
            <Button type="button" variant="outline" disabled>
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              Gravando…
            </Button>
          ) : null}

          {phase === 'recorded' ? (
            <>
              <Button
                type="button"
                onClick={handleConfirm}
                disabled={disabled || !recordedFile}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                Usar este vídeo
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleRerecord}
                disabled={disabled}
              >
                <RotateCcw className="w-4 h-4 mr-1.5" />
                Regravar
              </Button>
              {onCancel && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    revokeRecordedUrl();
                    setRecordedFile(null);
                    onCancel();
                  }}
                  disabled={disabled}
                >
                  Cancelar
                </Button>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
